import React, { useMemo } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';
import 'moment/locale/es';
import {
  Box,
  Spinner,
  Center,
  Text,
  Tooltip,
  useBreakpointValue
} from '@chakra-ui/react';

// Configurar moment en español
moment.locale('es');
const localizer = momentLocalizer(moment);

// Mensajes en español para react-big-calendar
const messages = {
  allDay: 'Todo el día',
  previous: 'Anterior',
  next: 'Siguiente',
  today: 'Hoy',
  month: 'Mes',
  week: 'Semana',
  day: 'Día',
  agenda: 'Agenda',
  date: 'Fecha',
  time: 'Hora',
  event: 'Evento',
  noEventsInRange: 'No hay eventos en este rango.',
  showMore: total => `+ Ver más (${total})`
};

// Paleta Datofy V1 — misma asignación usada en AgendaTodayCard / EventList
const CATEGORY_COLORS = {
  clase:      { bg: '#E2ECFB', ink: '#2A66B4' }, // sky
  reunion:    { bg: '#ECE6FB', ink: '#6849C4' }, // lav
  evaluacion: { bg: '#FBEECB', ink: '#946F12' }, // butter
  actividad:  { bg: '#DCF1E6', ink: '#1E7A4E' }, // mint
  importante: { bg: '#FBE2EC', ink: '#C03F76' }, // rose
  cita:       { bg: '#FDE7DB', ink: '#C0411A' }, // coral
  otro:       { bg: '#D6F0ED', ink: '#147F77' }, // teal
};
const DEFAULT_COLOR = CATEGORY_COLORS.otro;

function Calendar({
  view = 'month',
  date = new Date(),
  events = [],
  loading = false,
  onNavigate,
  onView,
  onSelectEvent,
  onSelectSlot,
  style = {},
  className = ''
}) {
  // Responsive values
  const calendarHeight = useBreakpointValue({ base: '500px', md: '600px', lg: '700px' });
  const fontSize = useBreakpointValue({ base: 'sm', md: 'md' });
  const padding = useBreakpointValue({ base: 2, md: 4 });

  // Formatear eventos para react-big-calendar
  const formattedEvents = useMemo(() => {
    return events.map(event => ({
      id: event.id,
      title: event.title,
      start: new Date(event.start_date),
      end: new Date(event.end_date),
      allDay: event.all_day || false,
      resource: {
        description: event.description,
        category: event.category,
        status: event.status,
        created_by: event.created_by,
        ...event
      }
    }));
  }, [events]);

  // Estilos del calendario
  const calendarStyle = {
    height: calendarHeight,
    width: '100%',
    backgroundColor: 'transparent',
    color: '#2B2723',
    fontSize: fontSize,
    ...style
  };

  // Estilos CSS — tokens Datofy V1 (coral / cream / ink)
  const customStyles = `
    .rbc-calendar {
      background-color: transparent;
      color: #2B2723;
      border: none;
      border-radius: 0;
      overflow: hidden;
      width: 100% !important;
      font-family: 'Plus Jakarta Sans', Inter, system-ui, -apple-system, sans-serif;
    }

    .rbc-header {
      background-color: #F4EEE7;
      border-bottom: 1px solid #EFE7DF;
      color: #6E655C;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      padding: 12px 8px;
      font-size: 11px;
    }

    .rbc-month-view,
    .rbc-time-view {
      border: 1px solid #EFE7DF;
      border-radius: 16px;
      overflow: hidden;
    }

    .rbc-month-row + .rbc-month-row,
    .rbc-day-bg + .rbc-day-bg {
      border-color: #EFE7DF;
    }

    .rbc-day-bg {
      background-color: #FFFFFF;
      border-right: 1px solid #EFE7DF;
    }

    .rbc-day-bg + .rbc-day-bg {
      border-left: none;
    }

    .rbc-off-range-bg {
      background-color: #FAF6F2;
    }

    .rbc-today {
      background-color: #FFF3EC !important;
    }

    .rbc-event {
      background-color: transparent !important;
      border: none !important;
      border-radius: 0 !important;
      color: inherit !important;
      font-size: inherit !important;
      padding: 1px !important;
      cursor: pointer;
      transition: all 0.2s;
    }

    .rbc-event:hover {
      transform: scale(1.02);
    }

    .rbc-event:focus,
    .rbc-day-slot .rbc-background-event:focus {
      outline: none !important;
    }

    .rbc-selected {
      background-color: #FDE7DB !important;
    }

    .rbc-date-cell {
      color: #2B2723;
      font-weight: 700;
      padding: 8px;
      text-align: right;
      font-size: ${fontSize === 'sm' ? '12px' : '13px'};
    }

    .rbc-date-cell.rbc-off-range {
      color: #A79C91;
    }

    .rbc-slot-selection {
      background-color: #FFF3EC;
      border: 2px solid #EF5A2A;
    }

    .rbc-time-header {
      border-bottom: 1px solid #EFE7DF;
    }

    .rbc-time-content {
      border-top: 1px solid #EFE7DF;
    }

    .rbc-time-slot {
      border-top: 1px solid #F4EEE7;
    }

    .rbc-timeslot-group {
      border-bottom: 1px solid #EFE7DF;
      min-height: 80px;
    }

    .rbc-show-more {
      background-color: #F4EEE7;
      color: #C0411A;
      font-weight: 700;
      border-radius: 999px;
      padding: 2px 8px;
      font-size: 11px;
      cursor: pointer;
      transition: all 0.2s;
    }

    .rbc-show-more:hover {
      background-color: #FDE7DB;
    }

    .rbc-current-time-indicator {
      background-color: #EF5A2A;
      height: 2px;
      position: relative;
    }

    .rbc-current-time-indicator::before {
      content: '';
      position: absolute;
      left: -4px;
      top: -4px;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: #EF5A2A;
    }

    .rbc-event, .rbc-day-slot .rbc-event {
      background-color: transparent !important;
      border: none !important;
      padding: 0 !important;
    }

    .rbc-row-segment {
      padding: 1px 2px;
    }

    @media (max-width: 768px) {
      .rbc-calendar {
        font-size: 12px;
      }

      .rbc-header {
        padding: 8px 4px;
        font-size: 10.5px;
      }

      .rbc-date-cell {
        padding: 4px;
        font-size: 12px;
      }
    }
  `;

  // Componente personalizado para eventos — pill Datofy V1
  const EventComponent = ({ event }) => {
    const palette = CATEGORY_COLORS[event.resource?.category] || DEFAULT_COLOR;

    return (
      <Tooltip label={event.title} hasArrow placement="top" bg="ink.900" color="white" borderRadius="8px" fontSize="12px">
        <Box
          bg={palette.bg}
          borderRadius="6px"
          px="6px"
          py="2px"
          width="100%"
          overflow="hidden"
          cursor="pointer"
          _hover={{ filter: 'brightness(0.97)' }}
          transition="filter 0.15s"
        >
          <Text
            fontSize="11px"
            fontWeight="700"
            color={palette.ink}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {event.title}
          </Text>
        </Box>
      </Tooltip>
    );
  };

  // Manejar selección de slot (crear evento)
  const handleSelectSlot = ({ start, end, slots, action }) => {
    if (onSelectSlot) {
      onSelectSlot({
        start,
        end,
        allDay: action === 'click' && slots.length === 1
      });
    }
  };

  // Loading state
  if (loading) {
    return (
      <Center h="600px">
        <Box textAlign="center">
          <Spinner size="xl" color="coral.500" />
          <Text mt={4} color="ink.700" fontFamily="jakarta">
            Cargando eventos...
          </Text>
        </Box>
      </Center>
    );
  }

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="cream.line"
      borderRadius="16px"
      boxShadow="0px 3.5px 5.5px rgba(0,0,0,0.02)"
      overflow="hidden"
      p={padding}
      fontFamily="jakarta"
      className={className}
    >
      {/* Estilos CSS inyectados */}
      <style>{customStyles}</style>

      <BigCalendar
        localizer={localizer}
        events={formattedEvents}
        startAccessor="start"
        endAccessor="end"
        titleAccessor="title"
        allDayAccessor="allDay"
        resourceAccessor="resource"
        style={calendarStyle}
        view={view}
        date={date}
        onNavigate={onNavigate}
        onView={onView}
        onSelectEvent={onSelectEvent}
        onSelectSlot={handleSelectSlot}
        selectable
        popup
        showMultiDayTimes
        step={15}
        timeslots={4}
        dayLayoutAlgorithm="no-overlap"
        messages={messages}
        toolbar={false}
        formats={{
          dateFormat: 'DD',
          dayFormat: (date, culture, localizer) =>
            localizer.format(date, 'dddd', culture),
          weekdayFormat: (date, culture, localizer) =>
            localizer.format(date, 'dddd', culture),
          monthHeaderFormat: (date, culture, localizer) =>
            localizer.format(date, 'MMMM YYYY', culture),
          dayHeaderFormat: (date, culture, localizer) =>
            localizer.format(date, 'dddd, DD MMMM', culture),
          dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
            `${localizer.format(start, 'DD MMMM', culture)} - ${localizer.format(end, 'DD MMMM YYYY', culture)}`,
          timeGutterFormat: (date, culture, localizer) =>
            localizer.format(date, 'HH:mm', culture),
          eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
            `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`,
          agendaTimeFormat: (date, culture, localizer) =>
            localizer.format(date, 'HH:mm', culture),
          agendaTimeRangeFormat: ({ start, end }, culture, localizer) =>
            `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`
        }}
        components={{
          event: EventComponent
        }}
        eventPropGetter={() => ({
          style: {
            backgroundColor: 'transparent',
            border: 'none',
            borderRadius: '0',
            padding: '1px'
          }
        })}
        dayPropGetter={(date) => ({
          style: {
            backgroundColor: moment(date).isSame(moment(), 'day') ? '#FFF3EC' : 'transparent'
          }
        })}
      />
    </Box>
  );
}

export default Calendar;
